import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { ApiError } from '../errors';
import { camelizeKeys } from '../normalize';

export {
  useMyDashboard,
  useMyDevices,
  useMyStations,
  useMySessions,
  useMyUsers,
  useUpdateProfile,
  useOmDashboardStats,
  useOmDashboardEnergyChart,
  useOmDashboardStations,
  useOmDashboardSessions,
  useOmStation,
  useOmSession,
  useOmInvitations,
  useOmOrganizationProfile,
  useUpdateOmOrganizationProfile,
  useOmDeviceCheck,
  useOmDeviceOwn,
  useOmDeviceUpdate,
  useOmDeviceDelete,
  useOmCreateStation,
  useOmUpdateStation,
  useOmDeleteStation,
  useOmLinkStationDevice,
  useOmUnlinkStationDevice,
  useOmInviteUser,
  useOmCancelInvitation,
  useOmResendInvitation,
  useOmUpdateUser,
  useOmRemoveUser,
  useOmActivateUser,
  useOmAdjustAllowance,
  useOmAllowanceHistory,
  useOmUserRfid,
  useOmUpsertUserRfid,
  useOmSendPasswordReset,
  useOmTestEmail,
  useOmTestSms,
  type OmUser,
  type OmDashboardStats,
  type OmEnergyChartPoint,
  type OmDashboardStation,
  type OmDashboardSession,
  type OmInvitation,
  type AllowanceHistoryEntry,
  type OmOrganizationProfile,
  type OmSessionDetail,
  type OmSessionListItem,
  type OmSessionListParams,
  type UpdateProfileInput,
} from './om';

export interface Notification {
  id: string;
  title: string;
  body?: string;
  type?: string;
  read: boolean;
  createdAt: string;
}

export function useNotifications() {
  return useQuery({
    queryKey: ['my', 'notifications'],
    queryFn: async (): Promise<{ data: Notification[] }> => {
      try {
        const raw = await api().get<unknown>('/my/notifications');
        const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
        const items = Array.isArray(obj.data) ? obj.data : [];
        return { data: items.map((n) => camelizeKeys<Notification>(n)) };
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return { data: [] };
        }
        throw err;
      }
    },
    refetchInterval: 30_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 501)) return false;
      return failureCount < 2;
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().post(`/my/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my', 'notifications'] }),
  });
}
